import { Injectable, inject } from '@angular/core';
import { common_urls } from 'src/environments/environment';
import { LogoutService } from './logout.service';
import { debounceTime, fromEvent, merge, Subject, Subscription, takeUntil } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private logoutService = inject(LogoutService);
  private oidcService = inject(OidcSecurityService);

  private readonly INACTIVITY_DURATION = 20 * 60 * 1000; //20 minutes in milliseconds
  private inactivityTimer: any;
  private activityEvents = [
    'mousemove', 'keydown', 'click', 'scroll',
    'touchstart', 'touchmove', 'touchend',
    'input',
    'orientationchange'
  ];
  private lastActivityTime: number = Date.now(); // track last activity timestamp
  private resetSubject = new Subject<void>(); // subject for timer reset events
  private logSubscription: Subscription; // Subscription for debounced logging
  private isTracking: boolean = false; // Flag to track if monitoring is active

  constructor() {
    this.logSubscription = this.resetSubject.pipe(
      debounceTime(50000)
    ).subscribe(() => {
      const elapsedTime = Date.now() - this.lastActivityTime;
      const remainingTime = this.INACTIVITY_DURATION - elapsedTime;
      if (remainingTime > 0) {
        const remainingMinutes = Math.floor(remainingTime / 1000 / 60);
        const remainingSeconds = Math.floor((remainingTime / 1000) % 60);
        console.log(`Time remaining before logout: ${remainingMinutes} minutes, ${remainingSeconds} seconds`);
        this.resetSubject.next();
      }
    })
  }

  /**
   * method to be called for sstarting inactivity watch
   */
  public startTrackingInactivity(): void {
    if (this.isTracking) {
      console.log('Inactivity tracking is already active.');
      return;
    }

    console.log('timer start');
    this.setActivityListeners();
    this.resetInactivityTimer();
    this.isTracking = true;
  }

  public stopTrackingInactivity(): void {
    if (!this.isTracking) {
      console.log('Inactivity tracking is not active.');
      return;
    }

    console.log('tracking stopped');
    this.removeActivityListeners();
    clearTimeout(this.inactivityTimer);
    this.inactivityTimer = undefined;
    this.logSubscription.unsubscribe();
    this.isTracking = false;
  }

  /**
   * setting up activity listener
   */
  private setActivityListeners(): void {
    this.activityEvents.forEach((event: string) => {
      document.addEventListener(event, this.resetInactivityTimer.bind(this), { passive: true });
    });
  }

  private removeActivityListeners(): void {
    this.activityEvents.forEach((event: string) => {
      document.removeEventListener(event, this.resetInactivityTimer.bind(this))
    })
  }

  private resetInactivityTimer(): void {
    clearTimeout(this.inactivityTimer);
    this.lastActivityTime = Date.now();
    this.inactivityTimer = setTimeout(() => this.logout(), this.INACTIVITY_DURATION);
    this.resetSubject.next()
  }

  /**
   * method to be called if inactivity timer needs to be stopped
   */
  public logout(): void {
    console.log('User logged out after 20 minutes of inactivity');
    this.logSubscription.unsubscribe();
    this.resetSubject.complete();
    this.isTracking = false;

    this.oidcService.logoffAndRevokeTokens().subscribe((result) => console.log(result));
    this.logoutService.OIDClogout(`${common_urls.cwsBaseUrl}/secur/logout.jsp`);
  }
}
